import { useEffect, useMemo, useState } from 'react'
import { Key, MoreHorizontal, Users } from 'lucide-react'
import { api, type DarkWebCredentialStatus, type DarkWebPerson } from '../../../lib/api'
import { useToast } from '../../../components/Toast'
import { Button, EmptyState, ErrorBanner, Menu, Pagination, StatusChip, Table, type Column, type SortState } from '../../../components/ui'
import { useDarkWebPage } from '../DarkWebContext'
import { PersonDrawer } from '../PersonDrawer'
import { AssignOwnerDialog, CreateTaskDialog } from '../PeopleDialogs'
import { useTenantUsers } from '../useTenantUsers'
import { SOURCE_TYPES, STATUS_LABEL, STATUS_TONE, formatDay, isOpenStatus, sourceTypeColor, sourceTypeLabel } from '../format'

const PAGE_SIZE = 50
const field = 'h-[38px] rounded-lg border border-line-strong bg-surface px-3 text-sm text-ink focus:outline-none focus-visible:ring-[3px] focus-visible:ring-focus'

type Progress = Awaited<ReturnType<typeof api.getDarkWebPeople>>['progress']

/**
 * People (employees) and Customers tabs: one row per exposed person, with remediation (#397 step 3d).
 */
export function PeopleTab({ category }: { category: 'employee' | 'customer' }) {
  const toast = useToast()
  const { canRemediate, peopleFilter, dataVersion, refreshStats } = useDarkWebPage()
  const users = useTenantUsers(canRemediate)

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [status, setStatus] = useState(peopleFilter.status || 'open')
  const [sourceType, setSourceType] = useState(peopleFilter.sourceType || '')
  const [owner, setOwner] = useState('')
  const [sort, setSort] = useState<SortState>({ key: 'last_seen', direction: 'desc' })
  const [offset, setOffset] = useState(0)

  const [people, setPeople] = useState<DarkWebPerson[]>([])
  const [total, setTotal] = useState(0)
  const [progress, setProgress] = useState<Progress | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Selection survives paging: email -> person (for their credential ids)
  const [selected, setSelected] = useState<Map<string, DarkWebPerson>>(new Map())
  const [drawerPerson, setDrawerPerson] = useState<DarkWebPerson | null>(null)
  const [ownerTargets, setOwnerTargets] = useState<DarkWebPerson[] | null>(null)
  const [taskTargets, setTaskTargets] = useState<DarkWebPerson[] | null>(null)
  const [busy, setBusy] = useState(false)

  const noun = category === 'customer' ? 'customers' : 'people'

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  // Filters change the result set - start from the first page with nothing selected
  useEffect(() => {
    setOffset(0)
    setSelected(new Map())
  }, [debouncedSearch, status, sourceType, owner, sort.key, sort.direction, category])

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const res = await api.getDarkWebPeople({
        category,
        status,
        sourceType: sourceType || undefined,
        search: debouncedSearch || undefined,
        owner: owner || undefined,
        sort: sort.key as 'last_seen',
        direction: sort.direction,
        limit: PAGE_SIZE,
        offset,
      })
      if (res.success) {
        setPeople(res.people)
        setTotal(res.total)
        setProgress(res.progress)
      } else {
        setError(res.error || 'The server returned an error.')
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Network error.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [category, debouncedSearch, status, sourceType, owner, sort.key, sort.direction, offset, dataVersion])

  async function afterChange(message: string) {
    toast.success(message)
    setSelected(new Map())
    await Promise.all([load(), refreshStats()])
  }

  async function setPeopleStatus(targets: DarkWebPerson[], newStatus: DarkWebCredentialStatus, note?: string) {
    setBusy(true)
    try {
      const ids = targets.flatMap(p => p.credentialIds)
      const res = await api.updateDarkWebCredentials(ids, { status: newStatus, notes: note })
      if (!res.success) throw new Error(res.error || res.message || 'Update failed')
      await afterChange(`${targets.length === 1 ? targets[0].email : `${targets.length} ${noun}`}: ${STATUS_LABEL[newStatus].toLowerCase()}`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update status')
    } finally {
      setBusy(false)
    }
  }

  async function assignOwner(ownerUserId: string | null) {
    if (!ownerTargets) return
    const res = await api.updateDarkWebCredentials(ownerTargets.flatMap(p => p.credentialIds), { ownerUserId })
    if (!res.success) {
      toast.error(res.error || 'Failed to assign owner')
      return
    }
    setOwnerTargets(null)
    await afterChange(ownerUserId ? 'Owner assigned' : 'Owner removed')
  }

  async function createTask(input: { assignedTo: string | null; dueDate: number | null; priority: string; note: string }) {
    if (!taskTargets) return
    const res = await api.createDarkWebPeopleTask({ emails: taskTargets.map(p => p.email), category, ...input })
    if (!res.success) {
      toast.error(res.error || 'Failed to create task')
      return
    }
    setTaskTargets(null)
    await afterChange(res.message || 'Task created')
  }

  const selectedPeople = [...selected.values()]
  const selectedKeys = useMemo(() => new Set(selected.keys()), [selected])

  function onSelectionChange(keys: Set<string>) {
    const next = new Map<string, DarkWebPerson>()
    for (const key of keys) {
      const person = selected.get(key) || people.find(p => p.email === key)
      if (person) next.set(key, person)
    }
    setSelected(next)
  }

  const columns: Array<Column<DarkWebPerson>> = [
    {
      key: 'email',
      header: category === 'customer' ? 'Customer' : 'Person',
      sortable: true,
      cell: p => (
        <button type="button" onClick={() => setDrawerPerson(p)} className="flex flex-col items-start gap-0.5 text-left focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-focus rounded">
          <span className="font-mono text-[12.5px] font-semibold text-ink hover:underline">{p.email}</span>
          <span className="text-xs text-muted">{p.exposureCount} exposure{p.exposureCount === 1 ? '' : 's'}</span>
        </button>
      ),
    },
    {
      key: 'source',
      header: 'Source',
      cell: p => (
        <div className="flex flex-col gap-1">
          {p.sourceTypes.map(t => (
            <span key={t} className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <span className={`h-2 w-2 rounded-sm ${sourceTypeColor(t)}`} aria-hidden="true" />
              {sourceTypeLabel(t)}
            </span>
          ))}
        </div>
      ),
    },
    {
      key: 'foundIn',
      header: 'Found in',
      cell: p => (
        <span className="text-ink-secondary">
          {p.foundIn.slice(0, 2).join(', ') || '—'}
          {p.foundInCount > 2 && <span className="text-muted"> +{p.foundInCount - 2} more</span>}
        </span>
      ),
    },
    {
      key: 'password',
      header: 'Password',
      cell: p => p.hasPassword
        ? <span className="inline-flex items-center gap-1 font-medium text-critical"><Key className="h-3 w-3" aria-hidden="true" />Exposed</span>
        : <span className="text-muted">No</span>,
    },
    { key: 'latest_leak', header: 'Latest leak', sortable: true, cell: p => <span className="whitespace-nowrap text-ink-secondary">{formatDay(p.latestLeakAt)}</span> },
    { key: 'status', header: 'Status', sortable: true, cell: p => <StatusChip tone={STATUS_TONE[p.status] || 'critical'}>{STATUS_LABEL[p.status] || p.status}</StatusChip> },
    { key: 'owner', header: 'Owner', cell: p => <span className="text-ink-secondary">{p.ownerName || 'Unassigned'}</span> },
    {
      key: 'actions',
      header: 'Actions',
      srOnlyHeader: true,
      align: 'right',
      cell: p => (
        <Menu
          items={[
            { key: 'view', label: 'View details', onSelect: () => setDrawerPerson(p) },
            ...(canRemediate ? (isOpenStatus(p.status) ? [
              { key: 'done', label: 'Password reset done', onSelect: () => setPeopleStatus([p], 'resolved', 'Password reset done') },
              { key: 'requested', label: 'Mark reset requested', onSelect: () => setPeopleStatus([p], 'reset_requested'), disabled: p.status === 'reset_requested' },
              { key: 'owner', label: 'Assign owner…', onSelect: () => setOwnerTargets([p]) },
              { key: 'task', label: 'Create task…', onSelect: () => setTaskTargets([p]) },
              { key: 'fp', label: 'False positive', onSelect: () => setPeopleStatus([p], 'false_positive') },
            ] : [
              { key: 'reopen', label: 'Reopen', onSelect: () => setPeopleStatus([p], 'new') },
            ]) : []),
          ]}
          trigger={props => (
            <button {...props} type="button" aria-label={`Actions for ${p.email}`} className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-md text-ink-secondary hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-focus">
              <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        />
      ),
    },
  ]

  const handledPct = progress && progress.total ? Math.round((progress.handled / progress.total) * 100) : 0
  const pct = (n: number) => (progress && progress.total ? `${(n / progress.total) * 100}%` : '0%')

  return (
    <div className="flex flex-col gap-4">
      {progress && progress.total > 0 && (
        <section aria-label="Remediation progress" className="flex flex-col gap-2.5 rounded-xl border border-line bg-surface px-5 py-4">
          <div className="flex flex-wrap justify-between gap-3">
            <span className="font-semibold">Remediation progress</span>
            <span className="text-[13px] text-ink-secondary">
              <strong className="text-ink">{progress.handled.toLocaleString()} of {progress.total.toLocaleString()}</strong> {noun} handled ({handledPct}%)
            </span>
          </div>
          <div className="flex h-2 gap-0.5 overflow-hidden rounded bg-surface-raised" aria-hidden="true">
            <div className="bg-good" style={{ width: pct(progress.resolved + progress.false_positive) }} />
            <div className="bg-link" style={{ width: pct(progress.reset_requested) }} />
            <div className="bg-warning" style={{ width: pct(progress.reviewed) }} />
          </div>
          <div className="flex flex-wrap gap-4 text-[13px] text-ink-secondary">
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-good" aria-hidden="true" />Resolved {(progress.resolved + progress.false_positive).toLocaleString()}</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-link" aria-hidden="true" />Reset requested {progress.reset_requested.toLocaleString()}</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-warning" aria-hidden="true" />Reviewed {progress.reviewed.toLocaleString()}</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm border border-line-strong bg-surface-raised" aria-hidden="true" />New {progress.new.toLocaleString()}</span>
          </div>
        </section>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-[220px] flex-1 flex-col gap-1.5">
          <label htmlFor={`dw-${category}-search`} className="text-[13px] font-medium text-ink-secondary">Search {noun}</label>
          <input id={`dw-${category}-search`} value={search} onChange={e => setSearch(e.target.value)} placeholder="Email or domain" className={field} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={`dw-${category}-status`} className="text-[13px] font-medium text-ink-secondary">Status</label>
          <select id={`dw-${category}-status`} value={status} onChange={e => setStatus(e.target.value)} className={field}>
            <option value="open">All open</option>
            <option value="new">New</option>
            <option value="reset_requested">Reset requested</option>
            <option value="reviewed">Reviewed</option>
            <option value="resolved">Resolved</option>
            <option value="false_positive">False positive</option>
            <option value="all">All</option>
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={`dw-${category}-source`} className="text-[13px] font-medium text-ink-secondary">Source</label>
          <select id={`dw-${category}-source`} value={sourceType} onChange={e => setSourceType(e.target.value)} className={field}>
            <option value="">All sources</option>
            {SOURCE_TYPES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        {canRemediate && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor={`dw-${category}-owner`} className="text-[13px] font-medium text-ink-secondary">Owner</label>
            <select id={`dw-${category}-owner`} value={owner} onChange={e => setOwner(e.target.value)} className={field}>
              <option value="">Anyone</option>
              <option value="none">Unassigned</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
            </select>
          </div>
        )}
      </div>

      {error && <ErrorBanner title={`Couldn't load ${noun}`} description={`${error} This does not mean no exposures were found.`} onRetry={load} />}

      {!error && (
        <Table
          caption={category === 'customer' ? 'Exposed customers' : 'Exposed people'}
          columns={columns}
          rows={people}
          rowKey={p => p.email}
          rowLabel={p => p.email}
          selected={canRemediate ? selectedKeys : undefined}
          onSelectionChange={canRemediate ? onSelectionChange : undefined}
          sort={sort}
          onSortChange={setSort}
          loading={loading}
          minWidth={980}
          empty={
            <EmptyState
              icon={<Users className="h-8 w-8" />}
              title={status === 'open' && !debouncedSearch && !sourceType && !owner ? `No ${noun} with open exposures` : `No ${noun} match these filters`}
              description={status === 'open' ? 'Resolved and false-positive exposures are hidden. Choose "All" to see them.' : undefined}
            />
          }
          toolbar={canRemediate && selected.size > 0 ? (
            <div className="flex flex-wrap items-center gap-3 border-b border-line bg-brand-tint px-4 py-2.5">
              <span className="font-semibold text-ink">{selected.size} selected</span>
              <Button size="sm" variant="primary" disabled={busy} onClick={() => setPeopleStatus(selectedPeople, 'resolved', 'Password reset done')}>Password reset done</Button>
              <Button size="sm" disabled={busy} onClick={() => setPeopleStatus(selectedPeople, 'reset_requested')}>Mark reset requested</Button>
              <Button size="sm" disabled={busy} onClick={() => setOwnerTargets(selectedPeople)}>Assign owner</Button>
              <Button size="sm" disabled={busy} onClick={() => setTaskTargets(selectedPeople)}>Create task</Button>
              <Button size="sm" disabled={busy} onClick={() => setPeopleStatus(selectedPeople, 'false_positive')}>False positive</Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Map())}>Clear selection</Button>
            </div>
          ) : undefined}
          footer={<Pagination offset={offset} limit={PAGE_SIZE} total={total} onChange={setOffset} noun={status === 'open' ? `open ${noun}` : noun} />}
        />
      )}

      <PersonDrawer
        person={drawerPerson}
        canEdit={canRemediate}
        onClose={() => setDrawerPerson(null)}
        onStatus={async (p, s, note) => {
          await setPeopleStatus([p], s, note)
          setDrawerPerson(prev => (prev ? { ...prev, status: s } : prev))
        }}
        onAssign={p => setOwnerTargets([p])}
        onCreateTask={p => setTaskTargets([p])}
      />
      <AssignOwnerDialog open={!!ownerTargets} count={ownerTargets?.length || 0} users={users} onClose={() => setOwnerTargets(null)} onAssign={assignOwner} />
      <CreateTaskDialog open={!!taskTargets} emails={(taskTargets || []).map(p => p.email)} users={users} onClose={() => setTaskTargets(null)} onCreate={createTask} />
    </div>
  )
}
